import { FC } from 'react';
// Redux
import { useAppSelector } from 'hooks/useStore';
import { getQuizAnalytics } from 'store/result/resultSelectors';
//
import ReactApexChart from "react-apexcharts";
// i18next
import { useTranslation } from 'react-i18next';
// utilities
import { getCountryName } from 'utilities/utilities';

const Charts:FC = () => {
  const { t } = useTranslation();

  const analytics = useAppSelector(getQuizAnalytics);
  
  const gender = [...(analytics?.gender || [])].sort((a:any,b:any) => b.value.localeCompare(a.value));
  const ageGroups = analytics?.ageGroups || [];
  const countries = [...(analytics?.countries || [])].sort(sortByCount);

  const total = countries.reduce((acc, curr) => acc + curr.count, 0);

  const countriesData = countries.reduce(
    (acc, curr) => {
      const percent = (curr.count / total) * 100;
      if (percent < 3) {
        acc.other += curr.count;
      } else {
        acc.data.push(curr);
      }
      return acc;
    },
    { data: [], other: 0 } as { data: typeof countries; other: number }
  );
  
  if (countriesData.other > 0) {
    countriesData.data.push({ value: 'Other', count: countriesData.other });
  }

  const getOptions = (labels: string[]):any => ({
    chart: {
      width: 380,
      type: 'pie',
    },
    labels,
    colors,
    responsive: [{
      breakpoint: 480,
      options: {
        chart: {
          width: 200
        },
        legend: {
          position: 'bottom'
        },
        dataLabels: {
          enabled: false,
        }
      }
    }],
    legend: {
      labels: {
        colors: '#eee'
      }
    },
    dataLabels: {
      style: {
        fontSize: '10px',
      }
    },
    tooltip: {
      y: {
        formatter: function (val: number, opts: any) {
          const series = opts.config.series;
          const total = series.reduce((sum: number, val: number) => sum + val, 0);
          const percent = (val / total) * 100;
          return `${val} (${percent.toFixed(1)}%)`;
        },
      },
    },
  })

  return (
    <div className="mb-8 flex flex-wrap items-center gap-4 flex-col lg:flex-row lg:justify-between">
      {gender.length ? (
        <div>
          <h3 className="mb-2 text-white font-bold text-center lg:text-left">{t('pages.quizAnalytics.genderLabel')}</h3>
          <ReactApexChart options={getOptions(gender.map(g => g.value))} series={gender.map(g => g.count)} type="pie" width={480} />
        </div>
      ) : null}
      {ageGroups.length ? (
        <div>
          <h3 className="mb-2 text-white font-bold text-center lg:text-left">{t('pages.quizAnalytics.ageLabel')}</h3>
          <ReactApexChart options={getOptions(ageGroups.map(a => a.value))} series={ageGroups.map(a => a.count)} type="pie" width={480} />
        </div>
      ) : null}
      {countries.length ? (
        <div>
          <h3 className="mb-2 text-white font-bold text-center lg:text-left">{t('pages.quizAnalytics.countryLabel')}</h3>
          <ReactApexChart options={getOptions(countriesData.data.map(c => getCountryName(c.value)))} series={countriesData.data.map(c => c.count)} type="pie" width={480} />
        </div>
      ) : null}
    </div>
  )
}

export default Charts;

const colors = [
  "#ff9000", "#0099ff", "#ff4081", "#00e676", "#ffd600",
  "#7c4dff", "#ff3d00", "#00bcd4", "#ff6f00", "#8bc34a",
  "#ff1744", "#00e5ff", "#293450", "#64dd17", "#ff80ab",
  "#1de9b6", "#ffc107", "#536dfe", "#c51162", "#00c853",
  "#ffab00", "#6200ea", "#aeea00", "#ff5722", "#2196f3",
  "#ff9100", "#3f51b5", "#76ff03", "#ff5252", "#009688",
  "#ffea00", "#673ab7", "#00cfff", "#ff7043", "#03a9f4",
  "#ea80fc", "#689f38", "#ffb74d", "#3d5afe", "#c62828"
]

const sortByCount = (a:any, b:any) => {
  if (a.count < b.count) return 1;
  if (a.count > b.count) return -1;
  return 0;
}
